import { useState, useEffect, useRef, useCallback } from 'react'
import { Bell, BellRing, Check, CheckCheck } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { notificationsApi, type NotificationInboxItem } from '@/api/notifications'

const POLL_INTERVAL_MS = 60_000
const MAX_DISPLAY = 10

const CATEGORY_LABELS: Record<string, string> = {
  deadline_reminder: 'Deadline',
  overdue_escalation: 'Overdue',
  morning_summary: 'Summary',
  task_assignment: 'Assigned',
  task_update: 'Updated',
  client_reminder: 'Client',
}

function getNotificationMessage(item: NotificationInboxItem): string {
  const v = item.variables
  if (v.task_title && v.days_until) {
    return `${v.task_title} — due in ${v.days_until} day(s)`
  }
  if (v.task_title && v.days_overdue) {
    return `${v.task_title} — ${v.days_overdue} day(s) overdue`
  }
  if (v.body) return v.body
  if (v.message_body) return v.message_body
  if (v.title) return v.title
  if (v.task_title) return v.task_title
  if (v.tasks_count) return `${v.tasks_count} overdue task(s) require attention`
  if (v.today_count) return `${v.today_count} task(s) due today, ${v.upcoming_count || '0'} upcoming`
  const TEMPLATE_LABELS: Record<string, string> = {
    'deadline_reminder_v2': 'Deadline reminder',
    'escalation_alert_v2': 'Overdue escalation',
    'daily_summary_v3': 'Morning summary',
    'task_notification_v1': 'Task notification',
    'escalation_digest_v1': 'Overdue task digest',
  }
  return TEMPLATE_LABELS[item.template] ?? item.template.replace(/_/g, ' ').replace(/v\d+$/, '').trim()
}

function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = Date.now()
  const diffMs = now - date.getTime()
  const diffSec = Math.floor(diffMs / 1000)

  if (diffSec < 60) return 'just now'
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay < 7) return `${diffDay}d ago`
  return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })
}

export function NotificationBell() {
  const [notifications, setNotifications] = useState<NotificationInboxItem[]>([])
  const [isOpen, setIsOpen] = useState(false)
  const [isMarkingAll, setIsMarkingAll] = useState(false)
  const panelRef = useRef<HTMLDivElement>(null)

  const fetchUnread = useCallback(async () => {
    try {
      const items = await notificationsApi.getInbox(true)
      setNotifications(Array.isArray(items) ? items : [])
    } catch {
      // Backend not available yet or error — show empty state
      setNotifications([])
    }
  }, [])

  // Poll for unread notifications
  useEffect(() => {
    fetchUnread()
    const interval = setInterval(fetchUnread, POLL_INTERVAL_MS)
    return () => clearInterval(interval)
  }, [fetchUnread])

  // Close panel on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      return () => document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  const handleMarkRead = async (id: string) => {
    try {
      await notificationsApi.markRead(id)
      setNotifications((prev) => prev.filter((n) => n.id !== id))
    } catch {
      // Silently fail — notification stays in list
    }
  }

  const handleMarkAllRead = async () => {
    setIsMarkingAll(true)
    try {
      await notificationsApi.markAllRead()
      setNotifications([])
    } catch {
      // Silently fail
    } finally {
      setIsMarkingAll(false)
    }
  }

  const unreadCount = notifications.length
  const displayItems = notifications.slice(0, MAX_DISPLAY)
  const BellIcon = unreadCount > 0 ? BellRing : Bell

  return (
    <div className="relative" ref={panelRef}>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen((prev) => !prev)}
        title="Notifications"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        className="h-9 w-9 relative"
      >
        <BellIcon className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-medium text-destructive-foreground">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 rounded-lg border border-border/50 bg-popover/95 backdrop-blur-xl shadow-xl z-50">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border/30">
            <span className="text-sm font-semibold">Notifications</span>
            {unreadCount > 0 && (
              <span className="text-xs text-muted-foreground">
                {unreadCount} unread
              </span>
            )}
          </div>

          {/* Notification list */}
          <div className="max-h-96 overflow-y-auto">
            {displayItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-8 px-4">
                <Bell className="h-8 w-8 text-muted-foreground/40 mb-2" />
                <p className="text-sm text-muted-foreground">No new notifications</p>
              </div>
            ) : (
              displayItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-muted/30 transition-colors border-b border-border/10 last:border-b-0 group"
                >
                  <div className="flex-1 min-w-0">
                    {item.category && (
                      <span className="text-[10px] font-medium uppercase tracking-wider text-primary/70">
                        {CATEGORY_LABELS[item.category] ?? item.category}
                      </span>
                    )}
                    <p className="text-sm leading-snug truncate">
                      {getNotificationMessage(item)}
                    </p>
                    <p className="text-[11px] text-muted-foreground mt-0.5">
                      {formatRelativeTime(item.created_at)}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    onClick={() => handleMarkRead(item.id)}
                    title="Mark as read"
                    aria-label="Mark as read"
                  >
                    <Check className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {unreadCount > 0 && (
            <div className="px-4 py-2.5 border-t border-border/30">
              <button
                onClick={handleMarkAllRead}
                disabled={isMarkingAll}
                className={cn(
                  'flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors w-full justify-center',
                  isMarkingAll && 'opacity-50 cursor-not-allowed'
                )}
              >
                <CheckCheck className="h-3.5 w-3.5" />
                {isMarkingAll ? 'Marking...' : 'Mark all as read'}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
